import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Modal } from 'components/Modal/Modal';
import ButtonV2 from 'components/ButtonV2';
import { CREATE_STRIPE_ONBOARDING_LINK } from './gql';

const ModalContent = styled.div`
  padding: 32px 24px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 8px 24px rgba(250, 70, 22, 0.3);

  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #000000;
  text-align: center;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: 20px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const BenefitIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(250, 70, 22, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;

  svg {
    width: 24px;
    height: 24px;
    color: #fa4616;
  }
`;

const BenefitTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 6px 0;
`;

const BenefitDescription = styled.p`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
`;

const SetupInfoBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
`;

const SetupInfoTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  margin: 0 0 12px 0;
`;

const SetupInfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SetupInfoItem = styled.li`
  font-size: 13px;
  color: #64748b;
  line-height: 1.8;
  padding-left: 24px;
  position: relative;
  margin-bottom: 8px;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #fa4616;
    font-weight: 700;
    font-size: 16px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonContainer = styled.div`
  margin-bottom: 16px;
`;

const ButtonHelper = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;

  strong {
    color: #475569;
    font-weight: 500;
  }
`;

const SkipLink = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  transition: color 0.2s;

  &:hover {
    color: #000000;
  }
`;

const SkipWarning = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;

  svg {
    width: 18px;
    height: 18px;
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SkipWarningText = styled.p`
  font-size: 13px;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
`;

export const PromoterOnboardingSuccessModal = ({
  isVisible,
  hideModal,
  businessName,
  accountId,
}) => {
  const [createStripeOnboardingLink, { loading: loadingStripeLink }] =
    useMutation(CREATE_STRIPE_ONBOARDING_LINK);
  const [stripeLink, setStripeLink] = useState(null);
  const [showSkipWarning, setShowSkipWarning] = useState(false);

  const fetchStripeLink = useCallback(async () => {
    if (!accountId) {
      toast.error('Account ID not found. Please try again.');
      return;
    }

    try {
      const result = await createStripeOnboardingLink({
        variables: { account_id: parseInt(accountId) },
      });
      if (result.data?.createStripeOnboardingLink?.url) {
        setStripeLink(result.data.createStripeOnboardingLink.url);
      }
    } catch (error) {
      console.error('Error fetching Stripe onboarding link:', error);
      toast.error('Failed to get Stripe setup link. Please try again.');
    }
  }, [accountId, createStripeOnboardingLink]);

  // Fetch Stripe onboarding link when modal becomes visible
  useEffect(() => {
    if (isVisible && !stripeLink && accountId) {
      fetchStripeLink();
    }
  }, [isVisible, accountId, stripeLink, fetchStripeLink]);

  const handleStripeSetup = () => {
    if (stripeLink) {
      window.location.href = stripeLink;
    } else {
      toast.error('Stripe link not available. Please try again.');
      fetchStripeLink();
    }
  };

  const handleSkipClick = () => {
    if (!showSkipWarning) {
      setShowSkipWarning(true);
    } else {
      hideModal();
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      hideModal={hideModal}
      noHeader
      maxWidth={650}
    >
      <ModalContent>
        <SuccessIcon>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </SuccessIcon>

        <Title>Let's Set Up Payments with Stripe</Title>

        <Subtitle>
          We've partnered with Stripe to make accepting payments safe, secure,
          and simple for your business
        </Subtitle>

        <BenefitsGrid>
          <BenefitCard>
            <BenefitIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </BenefitIcon>
            <BenefitTitle>Process Payments</BenefitTitle>
            <BenefitDescription>
              Handle all ticket sales and transactions securely
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </BenefitIcon>
            <BenefitTitle>Keep Money Safe</BenefitTitle>
            <BenefitDescription>
              Bank-level security and fraud protection built-in
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </BenefitIcon>
            <BenefitTitle>Get Paid Fast</BenefitTitle>
            <BenefitDescription>
              Weekly payouts directly to your bank account
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>

        <SetupInfoBox>
          <SetupInfoTitle>What to expect during setup:</SetupInfoTitle>
          <SetupInfoList>
            <SetupInfoItem>Takes about 5 minutes to complete</SetupInfoItem>
            <SetupInfoItem>
              You'll need your bank account and business details
            </SetupInfoItem>
            <SetupInfoItem>
              Stripe will guide you through each step
            </SetupInfoItem>
          </SetupInfoList>
        </SetupInfoBox>

        <ButtonContainer>
          <ButtonV2
            onClick={handleStripeSetup}
            disabled={loadingStripeLink || !stripeLink}
          >
            {loadingStripeLink ? 'Loading...' : 'Set Up Stripe Now →'}
          </ButtonV2>
          <ButtonHelper>
            <strong>Recommended:</strong> Complete payment setup now to start
            accepting payments. Takes about 5 minutes.
          </ButtonHelper>
        </ButtonContainer>

        <SkipLink onClick={handleSkipClick}>
          I'll do this later →
        </SkipLink>

        {showSkipWarning && (
          <SkipWarning>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <SkipWarningText>
              <strong>Important:</strong> You won't be able to accept payments
              or receive payouts until Stripe is set up. You can complete this
              anytime from Settings.
            </SkipWarningText>
          </SkipWarning>
        )}
      </ModalContent>
    </Modal>
  );
};
